import { System } from '../../../engine/ecs/core/System';
import { PacmanComponent, Direction } from '../components/PacmanComponent';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import { MovementComponent } from '../../../engine/ecs/components/MovementComponent';
import { SpriteComponent } from '../../../engine/ecs/components/SpriteComponent';
// AnimationComponent temporarily disabled due to interface issues
// import { AnimationComponent, AnimationState } from '../../../engine/ecs/components/AnimationComponent';
import { Vector2 } from '../../../engine/ecs/core/Vector2';
import { MazeData } from '../data/MazeData';
import { Entity } from '../../../engine/ecs/core/Entity';

/**
 * PacmanMovementSystem - Handles Pacman's grid-based movement
 * Simplified version without complex event handling
 */
export class PacmanMovementSystem extends System {
  private keysPressed: Set<string> = new Set();
  private mazeData: any; // Will be set when level loads

  constructor() {
    super();
    this.registerRequiredComponent(PacmanComponent);
    this.registerRequiredComponent(TransformComponent);
    this.registerRequiredComponent(MovementComponent);

    // Simple keyboard event handling
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', (event) => {
        this.keysPressed.add(event.key);
      });

      document.addEventListener('keyup', (event) => {
        this.keysPressed.delete(event.key);
      });
    }
  }

  /**
   * Set the current maze data for collision detection
   * This should be called whenever a level is initialized
   */
  public setMazeData(mazeData: any): void {
    this.mazeData = mazeData;
    console.log(`[PacmanMovementSystem] Maze data updated (walls: ${mazeData.width}x${mazeData.height})`);
  }

  private isKeyPressed(key: string): boolean {
    return this.keysPressed.has(key);
  }

  public update(deltaTime: number): void {
    // Require maze data to be set before movement (will be set by PacmanGame on level init)
    if (!this.mazeData) {
      console.warn('[PacmanMovementSystem] No maze data available, using fallback');
      this.mazeData = MazeData.getLevel(1);
    }

    const entities = this.getEntities();

    // Debug: Log occasionally to see if movement system is working
    if (Math.random() < 0.01) {
      // 1% of frames
      console.log(
        `PacmanMovementSystem: Processing ${entities.length} entities`
      );
      if (this.keysPressed.size > 0) {
        console.log(
          `PacmanMovementSystem: Keys pressed:`,
          Array.from(this.keysPressed)
        );
      }
    }

    for (const entity of entities) {
      const pacman = this.componentManager.getComponent(
        entity,
        PacmanComponent
      ) as PacmanComponent;
      const transform = this.componentManager.getComponent(
        entity,
        TransformComponent
      ) as TransformComponent;
      const movement = this.componentManager.getComponent(
        entity,
        MovementComponent
      ) as MovementComponent;

      // Debug: Log Pacman's alive status occasionally
      if (Math.random() < 0.01) {
        console.log(`PacmanMovementSystem: Pacman isAlive: ${pacman.isAlive}`);
      }

      if (!pacman.isAlive) {
        console.log('PacmanMovementSystem: Skipping dead Pacman');
        continue;
      }

      // Update eating animation timer
      if (pacman.isEating && pacman.eatingTimer > 0) {
        pacman.eatingTimer -= deltaTime;
        if (pacman.eatingTimer <= 0) {
          pacman.isEating = false;
        }
      }

      // Update invincibility timer
      if (pacman.invulnerabilityTimer > 0) {
        pacman.invulnerabilityTimer -= deltaTime;
        if (pacman.invulnerabilityTimer <= 0) {
          pacman.invulnerabilityTimer = 0;
          console.log('PacmanMovementSystem: Invincibility expired');
        }
      }

      // Handle input to set next direction
      this.handleInput(pacman);

      // Update movement based on current direction
      this.updateMovement(pacman, transform, movement, deltaTime);

      // Update sprite rendering data for generic rendering
      this.updateSpriteRenderingData(entity, pacman);

      // Handle tunnel wrapping
      this.handleTunnelWrap(transform);
    }
  }

  /**
   * Handle keyboard input for direction changes
   */
  private handleInput(pacman: PacmanComponent): void {
    let nextDirection = pacman.nextDirection;
    let inputDetected = false;

    if (
      this.isKeyPressed('ArrowUp') ||
      this.isKeyPressed('w') ||
      this.isKeyPressed('W')
    ) {
      nextDirection = Direction.Up;
      inputDetected = true;
    } else if (
      this.isKeyPressed('ArrowDown') ||
      this.isKeyPressed('s') ||
      this.isKeyPressed('S')
    ) {
      nextDirection = Direction.Down;
      inputDetected = true;
    } else if (
      this.isKeyPressed('ArrowLeft') ||
      this.isKeyPressed('a') ||
      this.isKeyPressed('A')
    ) {
      nextDirection = Direction.Left;
      inputDetected = true;
    } else if (
      this.isKeyPressed('ArrowRight') ||
      this.isKeyPressed('d') ||
      this.isKeyPressed('D')
    ) {
      nextDirection = Direction.Right;
      inputDetected = true;
    } else if (this.isKeyPressed('Enter')) {
      // Handle ENTER key for level completion
      console.log('PacmanMovementSystem: ENTER key pressed');
      // This will be handled by the game state system
      inputDetected = true;
    }

    // Debug: Log when input is detected
    if (inputDetected && Math.random() < 0.1) {
      // 10% chance when input detected
      console.log(
        `PacmanMovementSystem: Input detected - Direction: ${nextDirection}, Current: ${pacman.currentDirection}`
      );
    }

    pacman.nextDirection = nextDirection;
  }

  /**
   * Update Pacman's movement with smooth grid-based motion
   */
  private updateMovement(
    pacman: PacmanComponent,
    transform: TransformComponent,
    movement: MovementComponent,
    deltaTime: number
  ): void {
    const cellSize = this.mazeData.cellSize;
    const currentPos = transform.position;

    // Convert current position to grid coordinates
    const gridPos = MazeData.pixelToGrid(currentPos.x, currentPos.y, cellSize);

    // Try to change direction immediately if a new direction is requested
    if (pacman.nextDirection !== Direction.None) {
      const nextGridPos = this.getNextGridPosition(
        gridPos,
        pacman.nextDirection
      );

      if (
        MazeData.isValidPosition(
          this.mazeData.maze,
          nextGridPos.x,
          nextGridPos.y
        )
      ) {
        pacman.currentDirection = pacman.nextDirection;
        pacman.nextDirection = Direction.None;
        console.log(
          `PacmanMovementSystem: Direction changed to ${pacman.currentDirection}`
        );
      }
    }

    // Move in current direction if possible
    if (pacman.currentDirection !== Direction.None) {
      // Calculate movement velocity
      const velocity = this.getDirectionVector(pacman.currentDirection);
      velocity.x *= pacman.speed;
      velocity.y *= pacman.speed;

      movement.velocity = velocity;

      // Calculate potential new position
      let newX = transform.position.x + velocity.x * deltaTime;
      let newY = transform.position.y + velocity.y * deltaTime;

      // Keep Pacman centered on the opposite axis of movement
      if (
        pacman.currentDirection === Direction.Left ||
        pacman.currentDirection === Direction.Right
      ) {
        // Moving horizontally - keep Y centered on grid
        const currentGridY = Math.round((currentPos.y - 12) / cellSize);
        newY = currentGridY * cellSize + 12;
      } else if (
        pacman.currentDirection === Direction.Up ||
        pacman.currentDirection === Direction.Down
      ) {
        // Moving vertically - keep X centered on grid
        const currentGridX = Math.round((currentPos.x - 12) / cellSize);
        newX = currentGridX * cellSize + 12;
      }

      // Calculate target grid position based on movement direction
      let targetGridX: number;
      let targetGridY: number;

      // Simple approach: check if Pacman's center will move into a different grid cell
      // If moving right/down, check the next cell; if moving left/up, check current calculation
      switch (pacman.currentDirection) {
        case Direction.Left:
          targetGridX = Math.floor((newX - 12) / cellSize);
          targetGridY = Math.round((newY - 12) / cellSize);
          break;
        case Direction.Right:
          // Check if we're crossing into the next cell to the right
          targetGridX = Math.ceil((newX - 12) / cellSize);
          targetGridY = Math.round((newY - 12) / cellSize);
          break;
        case Direction.Up:
          targetGridX = Math.round((newX - 12) / cellSize);
          targetGridY = Math.floor((newY - 12) / cellSize);
          break;
        case Direction.Down:
          // Check if we're crossing into the next cell below
          targetGridX = Math.round((newX - 12) / cellSize);
          targetGridY = Math.ceil((newY - 12) / cellSize);
          break;
        default:
          targetGridX = Math.round((newX - 12) / cellSize);
          targetGridY = Math.round((newY - 12) / cellSize);
      }

      // Double safety check: validate both target grid position and ensure we're not moving into walls
      const isTargetValid = MazeData.isValidPosition(
        this.mazeData.maze,
        targetGridX,
        targetGridY
      );

      // Additional safety: check if we're trying to move into a wall by checking grid boundaries
      let canMove = true;
      if (
        targetGridX < 0 ||
        targetGridX >= this.mazeData.width ||
        targetGridY < 0 ||
        targetGridY >= this.mazeData.height
      ) {
        canMove = false;
      }

      // Debug: Log collision detection for right/down movements
      if (
        (pacman.currentDirection === Direction.Right ||
          pacman.currentDirection === Direction.Down) &&
        Math.random() < 0.1
      ) {
        console.log(
          `PacmanMovementSystem: Direction ${
            pacman.currentDirection
          }, Position (${newX.toFixed(1)}, ${newY.toFixed(
            1
          )}), Target Grid (${targetGridX}, ${targetGridY}), Valid: ${isTargetValid}`
        );
      }

      // Check if new position is valid BEFORE moving there
      if (isTargetValid && canMove) {
        // Safe to move - update position
        transform.position.x = newX;
        transform.position.y = newY;
      } else {
        // Hit a wall - stop moving but DON'T change position
        movement.velocity = new Vector2(0, 0);
        pacman.currentDirection = Direction.None;
        console.log(
          `PacmanMovementSystem: Hit wall, stopping at current position (${transform.position.x}, ${transform.position.y})`
        );
      }
    } else {
      // Not moving
      movement.velocity = new Vector2(0, 0);
    }
  }

  /**
   * Get the next grid position based on current position and direction
   */
  private getNextGridPosition(gridPos: Vector2, direction: Direction): Vector2 {
    const nextPos = gridPos.clone();

    switch (direction) {
      case Direction.Up:
        nextPos.y -= 1;
        break;
      case Direction.Down:
        nextPos.y += 1;
        break;
      case Direction.Left:
        nextPos.x -= 1;
        break;
      case Direction.Right:
        nextPos.x += 1;
        break;
    }

    return nextPos;
  }

  /**
   * Get direction vector for movement
   */
  private getDirectionVector(direction: Direction): Vector2 {
    switch (direction) {
      case Direction.Up:
        return new Vector2(0, -1);
      case Direction.Down:
        return new Vector2(0, 1);
      case Direction.Left:
        return new Vector2(-1, 0);
      case Direction.Right:
        return new Vector2(1, 0);
      default:
        return new Vector2(0, 0);
    }
  }

  /**
   * Update animation based on movement state
   * TODO: Implement when AnimationComponent interface is properly defined
   */
  private updateAnimation(_entity: Entity, _pacman: PacmanComponent): void {
    // Animation system disabled for now due to interface mismatch
    // Will be re-enabled when AnimationComponent is properly implemented
  }

  /**
   * Update sprite rendering data for generic rendering
   */
  private updateSpriteRenderingData(
    entity: Entity,
    pacman: PacmanComponent
  ): void {
    const sprite = this.componentManager.getComponent(
      entity,
      SpriteComponent
    ) as SpriteComponent;
    if (sprite) {
      // Convert Direction enum to string for generic rendering
      const directionString = this.directionToString(pacman.currentDirection);
      const movement = this.componentManager.getComponent(
        entity,
        MovementComponent
      ) as MovementComponent;
      const isMoving = movement && (movement.velocity.magnitude() > 0.1);
      
      sprite.renderingData = {
        direction: directionString,
        isEating: pacman.isEating,
        eatingTimer: pacman.eatingTimer,
        isMoving: isMoving,
        isSuperMode: pacman.isSuperMode || false,
      };
    }
  }

  /**
   * Convert Direction enum to string for generic rendering
   */
  private directionToString(direction: Direction): string {
    switch (direction) {
      case Direction.Up:
        return 'up';
      case Direction.Down:
        return 'down';
      case Direction.Left:
        return 'left';
      case Direction.Right:
        return 'right';
      default:
        return 'right';
    }
  }

  /**
   * Handle tunnel wrapping (teleporting from one side to the other)
   */
  private handleTunnelWrap(transform: TransformComponent): void {
    const screenWidth = this.mazeData.width * this.mazeData.cellSize;

    // Left tunnel to right tunnel
    if (transform.position.x < -12) {
      transform.position.x = screenWidth + 12;
    }
    // Right tunnel to left tunnel
    else if (transform.position.x > screenWidth + 12) {
      transform.position.x = -12;
    }
  }

}
